/* @flow */
/* eslint-env es6,browser */
import { IDP_COOKIE_EXPIRY_DAYS } from './idpconstants';
import {
  DEFAULT_STORAGE,
  getFromAllStore,
  isStorageSupported
} from '../utils/storageutils';

const DEFAULT_REFRESH_INTERVAL = -1; // turn off

/**
 * @global
 * @typedef perIDPConfig
 * @property {string} url URL for IDP
 * @property {string} pid unique ID
 * @property {number} expires cookie/cache expiry
 */

/**
 * @module _idpbase
 * @description IdentityProvider base module
 */

/**
 * @class _idpbase
 * @description IdentityProvider base class
 */

export default class _idpbase {
  _doc: Document;

  _win: WindowProxy;

  _name: string;

  _cachedID: string;

  _url: string;

  _pid: string;

  _nExpireDays: number;

  _storage: Array<string>;

  _refreshUrl: string;

  _refreshInterval: number;

  _idUpdateTime: number;

  _optoutKeys: Array<string>;

  _optedOut: boolean;

  /**
   * @class
   * @param {string} name - IDProvider name
   */
  constructor(name: string = '') {
    this._doc = document;
    this._win = window;
    this._name = name;
    this._cachedID = '';
    // config for partner
    this._url = '';
    this._pid = '';
    this._nExpireDays = IDP_COOKIE_EXPIRY_DAYS;
    this._storage = DEFAULT_STORAGE;
    this._idUpdateTime = 0;
    this._optoutKeys = [];
    this._optedOut = false;
  }

  /**
   * Initialize the object - called from adserver initialize
   *
   * @param {window} w - window object
   * @param {Document} d - document object
   * @param {module:globalParams.globalParams} [g] - global param object
   * @returns {undefined}
   */
  // eslint-disable-next-line no-unused-vars
  init(w: WindowProxy, d: Document, g: globalParam) {
    this._doc = d;
    this._win = w;
    this._url = '';
    this._pid = '';
    this._refreshUrl = '';
    this._refreshInterval = DEFAULT_REFRESH_INTERVAL;
    this._storage = DEFAULT_STORAGE;
    this._nExpireDays = IDP_COOKIE_EXPIRY_DAYS;
    this._idUpdateTime = 0;
  }

  _reset() {
    this._cachedID = '';
  }

  _checkOptOut() {
    // check if any one of the opt-out keys are set in cookie/local storage
    this._optedOut = this._optoutKeys.some(k => getFromAllStore(k).length > 0);

    // reset the partner specific data in cache/store as needed
    if (this._optedOut) {
      this._reset();
    }
  }

  _updateConfig({
    storage = DEFAULT_STORAGE,
    url = '',
    pid = '',
    refreshUrl = '',
    refreshInterval = DEFAULT_REFRESH_INTERVAL,
    expires = IDP_COOKIE_EXPIRY_DAYS
  }: perIDPConfig) {
    this._url = url;
    this._pid = pid;
    this._nExpireDays = expires;
    this._storage = storage;
    this._refreshUrl = refreshUrl;
    this._refreshInterval = refreshInterval;
  }

  _isSupported(): boolean {
    return isStorageSupported(this._storage);
  }

  _shouldRefresh(): boolean {
    return this._cachedID.length === 0;
  }

  shouldRefresh(): boolean {
    if (this._idUpdateTime === 0) {
      // first time. Let's fetch from server/storage
      return true;
    }
    if (this._refreshInterval > 0) {
      const curTime = Date.now();
      return curTime - this._idUpdateTime >= this._refreshInterval * 60 * 1000;
    }
    // Let each IDP client add extra check here.
    return this._shouldRefresh();
  }

  /**
   * check the local store and fetch the value if applicable
   * @returns {idStatus} ID loaded to cache
   */
  _loadFromCache(): boolean {
    return false;
  }

  /**
   * Update config - called from adserver updateConfig
   *
   * @param {perIDPConfig} [o] - adserver config from adconfig
   * @returns {undefined}
   */
  updateConfig(o: perIDPConfig) {
    this._updateConfig(o);
    // check if the parter is opted out
    this._checkOptOut();
    if (this._optedOut) {
      // nothing further to do here
      return;
    }
    if (!this._isSupported()) {
      return;
    }
    // try to load from Cache
    this._loadFromCache();
    if (this.shouldRefresh()) {
      this._refreshID();
      // success OR not, store the refresh timestamp
      this._idUpdateTime = Date.now();
    }
  }

  /**
   *
   *
   * @returns {*}  {string}
   * @memberof _idpbase
   */
  getName(): string {
    return this._name;
  }

  /**
   * Finds whether ID is enabled or not
   *
   * @returns {*} boolean
   * @memberof _idbpase
   */
  // eslint-disable-next-line no-unused-vars
  isEnabled(idpKey: string = ''): boolean {
    return !this._optedOut;
  }

  /**
   * Update cached ID if required
   *
   * @memberof _idpbase
   * @returns {undefined}
   */
  _refreshID() {}

  /**
   * Get id from this provider.
   * If it's async/server request, will return cached value
   *
   * @returns {*}  {string}
   * @memberof _idpbase
   * @param {string} idpKey - Key for ID module in Multi ID providers
   */
  // eslint-disable-next-line no-unused-vars
  getID(idpKey: string = ''): string | Array<string> {
    return this._cachedID;
  }

  /**
   * Get ext data from this provider.
   *
   * @returns {*}  {object}
   * @memberof _idpbase
   * @param {string} idpKey - Key for ID module in Multi ID providers
   */
  // eslint-disable-next-line no-unused-vars
  getExt(idpKey: string = ''): anyObject {
    return {};
  }
}
